import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from './redux/store';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';

// Pages - Cybers (utilisateurs internes)
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import ClientsList from './pages/ClientsList';
import ClientProfile from './pages/ClientProfileNew';
import ClientForm from './pages/ClientForm';
import DossiersARevoir from './pages/DossiersARevoir';
import InterventionsRequises from './pages/InterventionsRequises';
import DemarchesList from './pages/DemarchesList';
import DemarcheDetails from './pages/DemarcheDetails';
import MonitoringDashboard from './pages/MonitoringDashboard';
import Impayees from './pages/Impayees';
import FacturesList from './pages/FacturesList';
import CyberClientsPWA from './pages/CyberClientsPWA';
import CyberClientPWADetails from './pages/CyberClientPWADetails';

// Pages - Système Cyber
import CyberUsersManagement from './pages/CyberUsersManagement';
import CyberDashboard from './pages/CyberDashboard';
import CyberProfile from './pages/CyberProfile';
import TranslationsManagement from './pages/TranslationsManagement';

// Pages - Clients publics (PWA)
import LandingPage from './pages/LandingPage';
import TermsOfService from './pages/TermsOfService';
import ClientRegister from './pages/client/ClientRegister';
import ClientLogin from './pages/client/ClientLogin';
import ClientDashboard from './pages/client/ClientDashboard';
import ClientDossierDetails from './pages/client/ClientDossierDetails';
import ClientSubscription from './pages/client/ClientSubscription';
import ClientSettings from './pages/client/ClientSettings';
import ClientCyberManagement from './pages/client/ClientCyberManagement';
import LinkedAnefAccounts from './pages/client/LinkedAnefAccounts';
import ProtectedClientRoute from './components/ProtectedClientRoute';
import ProtectedRoute from './components/ProtectedRoute';
import AuthRedirect from './components/AuthRedirect';

function App() {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
        {/* Page d'accueil publique */}
        <Route path="/" element={isAuthenticated ? <AuthRedirect /> : <LandingPage />} />
        <Route path="/cgu" element={<TermsOfService />} />

        {/* Routes publiques - Cybers */}
        <Route
          path="/login"
          element={isAuthenticated ? <AuthRedirect /> : <Login />}
        />
        <Route
          path="/register"
          element={isAuthenticated ? <AuthRedirect /> : <Register />}
        />

        {/* Routes publiques - Clients PWA */}
        <Route path="/client/register" element={<ClientRegister />} />
        <Route path="/client/login" element={<ClientLogin />} />

        {/* Routes protégées - Clients PWA */}
        <Route
          path="/client/dashboard"
          element={
            <ProtectedClientRoute>
              <ClientDashboard />
            </ProtectedClientRoute>
          }
        />
        <Route
          path="/client/dossiers/:id"
          element={
            <ProtectedClientRoute>
              <ClientDossierDetails />
            </ProtectedClientRoute>
          }
        />
        <Route
          path="/client/abonnement"
          element={
            <ProtectedClientRoute>
              <ClientSubscription />
            </ProtectedClientRoute>
          }
        />
        <Route
          path="/client/settings"
          element={
            <ProtectedClientRoute>
              <ClientSettings />
            </ProtectedClientRoute>
          }
        />
        <Route
          path="/client/linked-anef-accounts"
          element={
            <ProtectedClientRoute>
              <LinkedAnefAccounts />
            </ProtectedClientRoute>
          }
        />
        <Route
          path="/client/cyber-management"
          element={
            <ProtectedClientRoute>
              <ClientCyberManagement />
            </ProtectedClientRoute>
          }
        />

        {/* Routes privées - Admin uniquement */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute adminOnly>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dossiers-a-revoir"
          element={
            <ProtectedRoute adminOnly>
              <DossiersARevoir />
            </ProtectedRoute>
          }
        />
        <Route
          path="/interventions-requises"
          element={
            <ProtectedRoute adminOnly>
              <InterventionsRequises />
            </ProtectedRoute>
          }
        />
        <Route
          path="/impayees"
          element={
            <ProtectedRoute adminOnly>
              <Impayees />
            </ProtectedRoute>
          }
        />
        <Route
          path="/factures"
          element={
            <ProtectedRoute adminOnly>
              <FacturesList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cyber-clients"
          element={
            <ProtectedRoute>
              <CyberClientsPWA />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cyber-clients/:clientAccountId"
          element={
            <ProtectedRoute>
              <CyberClientPWADetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cyber-users"
          element={
            <ProtectedRoute adminOnly>
              <CyberUsersManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/translations"
          element={
            <ProtectedRoute adminOnly>
              <TranslationsManagement />
            </ProtectedRoute>
          }
        />

        {/* Routes privées - Cyber uniquement */}
        <Route
          path="/cyber-dashboard"
          element={
            <ProtectedRoute cyberOnly>
              <CyberDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cyber-profile"
          element={
            <ProtectedRoute cyberOnly>
              <CyberProfile />
            </ProtectedRoute>
          }
        />
        {/* Routes privées - Admin et Cyber */}
        <Route
          path="/clients"
          element={
            <ProtectedRoute>
              <ClientsList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/clients/new"
          element={
            <ProtectedRoute>
              <ClientForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/clients/:id/edit"
          element={
            <ProtectedRoute>
              <ClientForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/clients/:id"
          element={
            <ProtectedRoute>
              <ClientProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/demarches"
          element={
            <ProtectedRoute>
              <DemarchesList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/demarches/:id"
          element={
            <ProtectedRoute>
              <DemarcheDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/monitoring"
          element={
            <ProtectedRoute>
              <MonitoringDashboard />
            </ProtectedRoute>
          }
        />

          {/* Route par défaut */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
